import { useRef, useCallback, useEffect } from 'react';
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import zoomPlugin from 'chartjs-plugin-zoom';
import { TelemetryLapData } from '../models/TelemetryData';
import { getLapColor, REF_LAP_ID } from '../components/telemetry/LapSelector';
import { TrackTurn, TURN_KEY_PREFIX, turnAnnotations } from '../utils/turnMarkers';

// Register Chart.js components
Chart.register(
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  Tooltip,
  Legend,
  Filler,
  annotationPlugin,
  zoomPlugin
);

// Chart.js defaults — refined for racing cockpit aesthetic
Chart.defaults.elements.line.borderWidth = 1.5;
Chart.defaults.elements.point.radius = 0;
Chart.defaults.elements.point.hoverRadius = 4;
Chart.defaults.color = '#aaaaaa';
Chart.defaults.animation = false;

// ── Left-to-right reveal plugin ──────────────────────────────────────────────
// Stores a 0→1 progress value per chart instance. The plugin clips the canvas
// to that fraction of the chart area width before drawing datasets, producing
// a left-to-right trace effect. Using a WeakMap avoids any Chart.js proxy issues.
const revealProgress = new WeakMap<object, number>();

function easeInOutQuart(t: number): number {
  return t < 0.5 ? 8 * t * t * t * t : 1 - Math.pow(-2 * t + 2, 4) / 2;
}

function revealChart(chart: object, duration: number, delay: number = 0): () => void {
  let cancelled = false;
  let rafId: number | undefined;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  const startReveal = () => {
    const start = performance.now();
    const tick = () => {
      if (cancelled) return;
      const raw = Math.min((performance.now() - start) / duration, 1);
      revealProgress.set(chart, easeInOutQuart(raw));
      (chart as any).draw();
      if (raw < 1) {
        rafId = requestAnimationFrame(tick);
      } else {
        revealProgress.delete(chart);
      }
    };
    rafId = requestAnimationFrame(tick);
  };

  if (delay > 0) {
    timeoutId = setTimeout(startReveal, delay);
  } else {
    startReveal();
  }

  return () => {
    cancelled = true;
    if (timeoutId !== undefined) clearTimeout(timeoutId);
    if (rafId !== undefined) cancelAnimationFrame(rafId);
    revealProgress.delete(chart);
  };
}

Chart.register({
  id: 'leftToRightReveal',
  beforeDatasetsDraw(chart: any) {
    const p = revealProgress.get(chart);
    if (p === undefined || p >= 1) return;
    const { ctx, chartArea: { left, right, top, bottom } } = chart;
    ctx.save();
    ctx.beginPath();
    ctx.rect(left, top - 10, (right - left) * p, bottom - top + 20);
    ctx.clip();
  },
  afterDatasetsDraw(chart: any) {
    if (revealProgress.has(chart)) chart.ctx.restore();
  },
});

type TelemetryChannel = 'b' | 't' | 'v' | 'w' | 'r' | 'g';

const KMH_TO_MPH = 0.621371;
const M_TO_FT = 3.28084;

interface ChartConfig {
  id: string;
  channel: TelemetryChannel;
  title: string;
  yMin?: number;
  yMax?: number;
  yTickFormatter?: (value: number) => string;
}

const DELTA_ZERO_LINE = {
  type: 'line',
  yMin: 0,
  yMax: 0,
  borderColor: 'rgba(212,168,67,0.35)',
  borderWidth: 1,
} as const;

export const CHART_CONFIGS: ChartConfig[] = [
  { id: 'brakeChart', channel: 'b', title: 'BRAKE', yMin: 0, yMax: 100 },
  { id: 'throttleChart', channel: 't', title: 'THROTTLE', yMin: 0, yMax: 100 },
  { id: 'speedChart', channel: 'v', title: 'SPEED', yTickFormatter: (v) => `${Math.round(v * KMH_TO_MPH)}` },
  { id: 'steeringChart', channel: 'w', title: 'STEERING' },
  { id: 'rpmChart', channel: 'r', title: 'RPM' },
  { id: 'gearChart', channel: 'g', title: 'GEAR', yMin: 0, yMax: 7 },
];

/**
 * The turn annotations of an existing set, cloned (Chart.js hands back proxies that loop if
 * re-assigned). Every rebuild of the annotation set keeps them, as it keeps the sector lines.
 */
export function keepTurnAnnotations(existing: Record<string, any>): Record<string, any> {
  const kept: Record<string, any> = {};
  for (const [key, value] of Object.entries(existing || {})) {
    if (key.startsWith(TURN_KEY_PREFIX)) kept[key] = JSON.parse(JSON.stringify(value));
  }
  return kept;
}

function generateData(
  telemetry: TelemetryLapData,
  channel: TelemetryChannel
): { x: number; y: number }[] {
  const series: { x: number; y: number }[] = [];
  const frames = telemetry.t;
  const keys = Object.keys(frames).sort((a, b) => parseFloat(a) - parseFloat(b));

  for (const key of keys) {
    const frame = frames[key];
    series.push({ x: parseFloat(key), y: frame[channel] });
  }
  return series;
}

export function calculateDelta(
  baseTelemetry: TelemetryLapData,
  compTelemetry: TelemetryLapData
): { x: number; y: number }[] {
  const series: { x: number; y: number }[] = [];
  const baseKeys = Object.keys(baseTelemetry.t);
  const compKeys = Object.keys(compTelemetry.t);

  // Use the shorter set of keys
  const keys = baseKeys.length <= compKeys.length ? baseKeys : compKeys;

  for (const key of keys) {
    const baseFrame = baseTelemetry.t[key];
    const compFrame = compTelemetry.t[key];
    if (!baseFrame || !compFrame) continue;

    // Use recorded timestamp — (comp time - base time) in seconds
    series.push({ x: parseFloat(key), y: (compFrame.m - baseFrame.m) / 1000 });
  }
  return series;
}

export function smoothDelta(data: { x: number; y: number }[], window = 5): { x: number; y: number }[] {
  const half = Math.floor(window / 2);
  return data.map((p, i) => {
    const start = Math.max(0, i - half);
    const end = Math.min(data.length - 1, i + half);
    let sum = 0;
    for (let j = start; j <= end; j++) sum += data[j].y;
    return { x: p.x, y: (sum / (end - start + 1)) };
  });
}

// Find the closest data point in a dataset to a given x value
function findClosestPoint(data: { x: number; y: number }[], xValue: number): { x: number; y: number } | null {
  if (data.length === 0) return null;
  return data.reduce((prev, curr) =>
    Math.abs(curr.x - xValue) < Math.abs(prev.x - xValue) ? curr : prev
  );
}

interface UseTelemetryChartsOptions {
  onChartClick?: (distanceMarker: number) => void;
  onZoomChange?: (min: number | null, max: number | null) => void;
  onCursorValues?: (values: Map<string, Map<string, number>>) => void;
}

export function useTelemetryCharts(options: UseTelemetryChartsOptions = {}) {
  const chartsRef = useRef<Record<string, Chart>>({});
  const onChartClickRef = useRef(options.onChartClick);
  const onZoomChangeRef = useRef(options.onZoomChange);
  const onCursorValuesRef = useRef(options.onCursorValues);
  // Track current x position for arrow key navigation
  const xValuesRef = useRef<number[]>([]);
  const currentXIndexRef = useRef(0);
  const pendingCancelsRef = useRef<Array<() => void>>([]);

  useEffect(() => {
    onChartClickRef.current = options.onChartClick;
  }, [options.onChartClick]);

  useEffect(() => {
    onZoomChangeRef.current = options.onZoomChange;
  }, [options.onZoomChange]);

  useEffect(() => {
    onCursorValuesRef.current = options.onCursorValues;
  }, [options.onCursorValues]);

  // Deep-clone an annotation config to strip Chart.js proxy wrappers.
  // Passing proxied annotation objects back into a new annotations set
  // triggers recursive setter calls → infinite loop / stack overflow.
  const cloneAnnotation = (obj: any): any => JSON.parse(JSON.stringify(obj));

  const addVerticalLineWithLabels = useCallback((xValue: number) => {
    Object.values(chartsRef.current).forEach((chart) => {
      // Preserve sector annotations, replace cursor line
      const existing = (chart.options.plugins!.annotation as any).annotations || {};
      const annotations: Record<string, any> = {
        verticalLine: {
          type: 'line',
          xMin: xValue,
          xMax: xValue,
          borderColor: '#00daf3',
          borderWidth: 1,
          borderDash: [4, 3],
        },
      };
      // Clone sector annotations to strip proxy wrappers
      if (existing.sectorS1) annotations.sectorS1 = cloneAnnotation(existing.sectorS1);
      if (existing.sectorS2) annotations.sectorS2 = cloneAnnotation(existing.sectorS2);
      if (existing.zeroLine) annotations.zeroLine = DELTA_ZERO_LINE;
      Object.assign(annotations, keepTurnAnnotations(existing));

      // Build label content from all datasets
      const lines: string[] = [`${Math.round(xValue * M_TO_FT)} ft`];
      chart.data.datasets.forEach((dataset) => {
        const data = dataset.data as { x: number; y: number }[];
        const closest = findClosestPoint(data, xValue);
        if (closest) {
          lines.push(`${dataset.label}: ${closest.y.toFixed(2)}`);
          // Store for xValues navigation
          if (chart === Object.values(chartsRef.current)[0]) {
            xValuesRef.current = data.map((p) => p.x);
            currentXIndexRef.current = data.indexOf(closest);
            if (currentXIndexRef.current === -1) {
              currentXIndexRef.current = data.findIndex((p) => p.x === closest.x);
            }
          }
        }
      });

      // Per-lap values are shown in chart headers instead of inline labels

      (chart.options.plugins!.annotation as any).annotations = annotations;
      chart.update('none');
    });

    // Build per-chart values map
    const allChartValues = new Map<string, Map<string, number>>();
    Object.entries(chartsRef.current).forEach(([chartId, chart]) => {
      const chartValues = new Map<string, number>();
      chart.data.datasets.forEach((dataset) => {
        const data = ((chartId === 'deltaChart' && (dataset as any).rawData) || dataset.data) as { x: number; y: number }[];
        const closest = findClosestPoint(data, xValue);
        if (closest && dataset.label) {
          chartValues.set(dataset.label, closest.y);
        }
      });
      allChartValues.set(chartId, chartValues);
    });
    onCursorValuesRef.current?.(allChartValues);
  }, []);

  const clearAnnotations = useCallback(() => {
    Object.values(chartsRef.current).forEach((chart) => {
      const existing = (chart.options.plugins!.annotation as any).annotations || {};
      // Build fresh object — clone to strip proxy wrappers (causes infinite loop)
      const annotations: Record<string, any> = {};
      if (existing.sectorS1) annotations.sectorS1 = cloneAnnotation(existing.sectorS1);
      if (existing.sectorS2) annotations.sectorS2 = cloneAnnotation(existing.sectorS2);
      if (existing.zeroLine) annotations.zeroLine = DELTA_ZERO_LINE;
      Object.assign(annotations, keepTurnAnnotations(existing));
      (chart.options.plugins!.annotation as any).annotations = annotations;
      chart.update('none');
    });
    xValuesRef.current = [];
    currentXIndexRef.current = 0;
  }, []);

  const setSectorAnnotations = useCallback((sectorDistances: { s1End: number | null; s2End: number | null } | null) => {
    Object.values(chartsRef.current).forEach((chart) => {
      const existing = (chart.options.plugins!.annotation as any).annotations || {};
      // Build fresh object — clone to strip proxy wrappers (causes infinite loop)
      const annotations: Record<string, any> = {};

      // Preserve cursor annotation if present
      if (existing.verticalLine) annotations.verticalLine = cloneAnnotation(existing.verticalLine);
      // Preserve delta zero line if present (use constant, never read back from proxy)
      if (existing.zeroLine) annotations.zeroLine = DELTA_ZERO_LINE;
      Object.assign(annotations, keepTurnAnnotations(existing));

      if (sectorDistances) {
        // S1 | S2 boundary
        if (sectorDistances.s1End !== null) {
          annotations.sectorS1 = {
            type: 'line',
            xMin: sectorDistances.s1End,
            xMax: sectorDistances.s1End,
            borderColor: '#444444',
            borderWidth: 1,
            borderDash: [6, 4],
            label: {
              display: true,
              content: 'S1 | S2',
              position: 'start',
              color: '#aaaaaa',
              font: { size: 8, family: "'Roboto Condensed', sans-serif" },
              backgroundColor: 'transparent',
            },
          };
        }

        // S2 | S3 boundary (only drawn when sec3 > 0, signaled by non-null s2End)
        if (sectorDistances.s2End !== null) {
          annotations.sectorS2 = {
            type: 'line',
            xMin: sectorDistances.s2End,
            xMax: sectorDistances.s2End,
            borderColor: '#444444',
            borderWidth: 1,
            borderDash: [6, 4],
            label: {
              display: true,
              content: 'S2 | S3',
              position: 'start',
              color: '#aaaaaa',
              font: { size: 8, family: "'Roboto Condensed', sans-serif" },
              backgroundColor: 'transparent',
            },
          };
        }
      }

      (chart.options.plugins!.annotation as any).annotations = annotations;
      chart.update('none');
    });
  }, []);

  /**
   * Official turns on every chart; "Turn N" labels on the speed chart only, so the stacked
   * charts don't repeat them. An empty list (layout not fully mapped) removes them all.
   */
  const setTurnAnnotations = useCallback((turns: TrackTurn[], trackLength: number) => {
    Object.entries(chartsRef.current).forEach(([chartId, chart]) => {
      const existing = (chart.options.plugins!.annotation as any).annotations || {};
      const annotations: Record<string, any> = {};
      for (const [key, value] of Object.entries(existing)) {
        if (key.startsWith(TURN_KEY_PREFIX)) continue;
        annotations[key] = key === 'zeroLine' ? DELTA_ZERO_LINE : cloneAnnotation(value);
      }
      Object.assign(annotations, turnAnnotations(turns, trackLength, chartId === 'speedChart'));
      (chart.options.plugins!.annotation as any).annotations = annotations;
      chart.update('none');
    });
  }, []);

  const synchronizeZoom = useCallback((sourceChart: Chart) => {
    const xScale = sourceChart.scales.x;
    const min = xScale.min;
    const max = xScale.max;

    Object.values(chartsRef.current).forEach((chart) => {
      if (chart !== sourceChart) {
        chart.options.scales!.x!.min = min;
        chart.options.scales!.x!.max = max;
        chart.update('none');
      }
    });

    onZoomChangeRef.current?.(min, max);
  }, []);

  const createChart = useCallback(
    (
      canvasId: string,
      chartId: string,
      distanceMarkers: string[],
      trackLength: number
    ) => {
      const canvas = document.getElementById(canvasId) as HTMLCanvasElement;
      if (!canvas) return;

      if (chartsRef.current[chartId]) {
        chartsRef.current[chartId].destroy();
      }

      const chart = new Chart(canvas, {
        type: 'line',
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              type: 'linear',
              min: 0,
              max: trackLength,
              display: true,
              grid: { color: 'rgba(255,255,255,0.03)', lineWidth: 0.5 },
              border: { color: '#2a2020' },
              ticks: {
                color: '#aaaaaa',
                font: { size: 9, family: "'Space Grotesk', sans-serif" },
                callback: (value: any) => `${Math.round(value * M_TO_FT)} ft`,
                maxTicksLimit: 10,
              },
            },
            y: {
              display: true,
              grid: { color: 'rgba(255,255,255,0.03)', lineWidth: 0.5 },
              border: { color: '#2a2020' },
              ticks: { color: '#aaaaaa', font: { size: 9, family: "'Space Grotesk', sans-serif" } },
              afterFit: (axis: any) => {
                axis.width = 50; // Fixed width so all charts align
              },
            },
          },
          plugins: {
            legend: { display: false },
            annotation: { annotations: {} },
            zoom: {
              zoom: {
                drag: { enabled: true },
                mode: 'x',
                onZoom: ({ chart: zoomedChart }) => {
                  synchronizeZoom(zoomedChart);
                },
              },
              pan: {
                enabled: true,
                mode: 'x',
                modifierKey: 'shift' as const,
                onPanComplete: ({ chart: pannedChart }) => {
                  synchronizeZoom(pannedChart);
                },
              },
            },
          },
          onClick: (_event, _elements, chart) => {
            const evt = _event as unknown as { native: MouseEvent };
            if (evt.native && chart.scales.x) {
              const xValue = chart.scales.x.getValueForPixel(evt.native.offsetX);
              if (xValue != null) {
                addVerticalLineWithLabels(xValue);
                onChartClickRef.current?.(xValue);
              }
            }
          },
        },
        data: {
          labels: distanceMarkers,
          datasets: [],
        },
      });

      const config = CHART_CONFIGS.find((c) => c.id === chartId);
      if (config) {
        if (config.yMin !== undefined) chart.options.scales!.y!.min = config.yMin;
        if (config.yMax !== undefined) chart.options.scales!.y!.max = config.yMax;
        if (config.yTickFormatter) {
          (chart.options.scales!.y!.ticks as any).callback = config.yTickFormatter;
        }
      }

      if (chartId === 'deltaChart') {
        (chart.options.plugins!.annotation as any).annotations = { zeroLine: DELTA_ZERO_LINE };
        (chart.options.scales!.y!.ticks as any).callback = (v: number) => v.toFixed(2);
      }

      chartsRef.current[chartId] = chart;
      return chart;
    },
    [addVerticalLineWithLabels, synchronizeZoom]
  );

  const updateDatasets = useCallback(
    (
      lapDataMap: Map<string, TelemetryLapData>,
      checkedLapIds: string[],
      baselineLapId: string | null,
      lapLabels?: Map<string, string>,
      allLapIds?: string[],
      animateIn?: boolean
    ) => {
      CHART_CONFIGS.forEach((config, index) => {
        const chart = chartsRef.current[config.id];
        if (!chart) return;

        chart.data.datasets = checkedLapIds.map((lapId) => {
          const telemetry = lapDataMap.get(lapId);
          const data = telemetry ? generateData(telemetry, config.channel) : [];
          const dataset: any = {
            label: lapLabels?.get(lapId) || `Lap ${lapId.substring(0, 6)}`,
            data,
            borderColor: getLapColor(lapId, allLapIds || checkedLapIds),
            backgroundColor: 'rgba(0, 0, 0, 0)',
            fill: false,
          };
          if (lapId === REF_LAP_ID) {
            dataset.borderDash = [6, 3];
          }
          return dataset;
        });

        if (animateIn) {
          revealProgress.set(chart, 0);
          chart.update('none');
          pendingCancelsRef.current.push(revealChart(chart, 3000, index * 250));
        } else {
          chart.update('none');
        }
      });

      const deltaChart = chartsRef.current['deltaChart'];
      if (deltaChart) {
        const refTelemetry = lapDataMap.get(REF_LAP_ID);

        if (refTelemetry && baselineLapId) {
          // Reference mode: each session lap gets a delta line against the reference
          const sessionLapIds = checkedLapIds.filter((id) => id !== REF_LAP_ID);
          deltaChart.data.datasets = sessionLapIds
            .map((lapId) => {
              const compTelemetry = lapDataMap.get(lapId);
              if (!compTelemetry) return null;
              const rawDelta = calculateDelta(refTelemetry, compTelemetry);
              return {
                label: lapLabels?.get(lapId) || `Lap ${lapId.substring(0, 6)}`,
                data: smoothDelta(rawDelta, 10),
                rawData: rawDelta,
                borderColor: getLapColor(lapId, allLapIds || checkedLapIds),
                backgroundColor: 'rgba(0, 0, 0, 0)',
                fill: false,
                cubicInterpolationMode: 'monotone' as const,
                tension: 0.4,
              };
            })
            .filter((d): d is NonNullable<typeof d> => d !== null);
        } else if (baselineLapId && checkedLapIds.length >= 2) {
          // Fallback: original single-delta behavior
          const baseTelemetry = lapDataMap.get(baselineLapId);
          const compLapId = checkedLapIds.find((id) => id !== baselineLapId);
          const compTelemetry = compLapId ? lapDataMap.get(compLapId) : undefined;

          if (baseTelemetry && compTelemetry) {
            deltaChart.data.datasets = [
              (() => {
                const rawDelta = calculateDelta(baseTelemetry, compTelemetry);
                return {
                  label: 'Delta',
                  data: smoothDelta(rawDelta, 10),
                  rawData: rawDelta,
                  borderColor: '#ff9800',
                  backgroundColor: 'rgba(0, 0, 0, 0)',
                  fill: false,
                  cubicInterpolationMode: 'monotone' as const,
                  tension: 0.4,
                };
              })(),
            ];
          } else {
            deltaChart.data.datasets = [];
          }
        } else {
          deltaChart.data.datasets = [];
        }
        if (animateIn) {
          revealProgress.set(deltaChart, 0);
          deltaChart.update('none');
          pendingCancelsRef.current.push(revealChart(deltaChart, 3000, CHART_CONFIGS.length * 250));
        } else {
          deltaChart.update('none');
        }
      }
    },
    []
  );

  const setChartZoomRange = useCallback((min: number, max: number) => {
    Object.values(chartsRef.current).forEach((chart) => {
      chart.options.scales!.x!.min = min;
      chart.options.scales!.x!.max = max;
      chart.update('none');
    });
    onZoomChangeRef.current?.(min, max);
  }, []);

  const resetZoom = useCallback(() => {
    Object.values(chartsRef.current).forEach((chart) => {
      chart.resetZoom();
    });
    onZoomChangeRef.current?.(null, null);
  }, []);

  // Arrow key navigation between data points
  const navigateByArrowKey = useCallback((direction: 'left' | 'right', shift: boolean) => {
    if (xValuesRef.current.length === 0) return;

    const skip = shift ? 1 : 5;
    if (direction === 'right') {
      currentXIndexRef.current = Math.min(currentXIndexRef.current + skip, xValuesRef.current.length - 1);
    } else {
      currentXIndexRef.current = Math.max(currentXIndexRef.current - skip, 0);
    }

    const xValue = xValuesRef.current[currentXIndexRef.current];
    addVerticalLineWithLabels(xValue);
    onChartClickRef.current?.(xValue);
  }, [addVerticalLineWithLabels]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      pendingCancelsRef.current.forEach((cancel) => cancel());
      pendingCancelsRef.current = [];
      Object.values(chartsRef.current).forEach((chart) => chart.destroy());
      chartsRef.current = {};
    };
  }, []);

  return {
    chartsRef,
    createChart,
    updateDatasets,
    addVerticalLine: addVerticalLineWithLabels,
    clearAnnotations,
    setSectorAnnotations,
    setTurnAnnotations,
    setChartZoomRange,
    resetZoom,
    synchronizeZoom,
    navigateByArrowKey,
  };
}
